(function () {
  'use strict';

  var STORAGE_KEY = 'ayami_size_profile';
  var PENDING_SAVE_KEY = 'size_profile_pending_save';
  var RESUME_AFTER_LOGIN_KEY = 'size_profile_resume_after_login';
  var AUTO_SAVE_PARAM = 'size_profile_auto_save';
  var SAVE_PATH = '/apps/ayami-color-guide/size-save';
  var CM_PER_INCH = 2.54;
  var chartsCache = null;
  var chartsPromise = null;
  var recommendSeq = 0;

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isHtmlResponse(text) {
    var trimmed = String(text || '').trim().toLowerCase();
    return trimmed.indexOf('<!doctype') === 0 || trimmed.indexOf('<html') === 0;
  }

  function resolveSaveUrl(raw) {
    var path = SAVE_PATH;
    try {
      if (raw) {
        if (/^https?:\/\//i.test(raw)) {
          var absolute = new URL(raw);
          if (absolute.origin === window.location.origin) {
            path = absolute.pathname;
          }
        } else if (String(raw).charAt(0) === '/') {
          path = String(raw).split('?')[0];
        }
      }
    } catch (e) {
      path = SAVE_PATH;
    }
    path = path.replace(/^\/(en|es|fr|de|ca|it|pl|sv)(?=\/apps\/)/i, '');
    if (path.indexOf('/apps/ayami-color-guide/size-save') !== 0) {
      path = SAVE_PATH;
    }
    return new URL(path, window.location.origin).toString();
  }

  function getConfig(root) {
    return {
      loggedIn: root.getAttribute('data-customer-logged-in') === 'true',
      customerId: root.getAttribute('data-customer-id') || null,
      saveUrl: resolveSaveUrl(root.getAttribute('data-save-url')),
      loginUrl: root.getAttribute('data-login-url') || '/account/login',
      registerUrl: root.getAttribute('data-register-url') || '/account/register',
      sizePage: root.getAttribute('data-size-page') || window.location.pathname,
      nextUrl: root.getAttribute('data-next-url') || '/pages/preferences',
      nextLabel: root.getAttribute('data-next-label') || 'Go to your preferences',
      shopUrl: root.getAttribute('data-shop-url') || '/pages/preferences',
      chartsUrl: root.getAttribute('data-charts-url') || '',
      contactUrl: root.getAttribute('data-contact-url') || '/pages/contact',
    };
  }

  function resolveNextUrl(raw) {
    var fallback = '/pages/preferences';
    var value = String(raw || fallback).trim() || fallback;
    try {
      var parsed = new URL(value, window.location.origin);
      return parsed.pathname + parsed.search + parsed.hash;
    } catch (e) {
      return value;
    }
  }

  function showInlineSizeBridge(root, payload) {
    var quiz = root.querySelector('[data-size-profile-quiz]');
    var heading = root.querySelector('[data-size-profile-heading]');
    var intro = root.querySelector('[data-size-profile-intro]');
    var bridge = root.querySelector('[data-size-onboarding-bridge]');
    if (!bridge) return false;

    if (quiz) quiz.classList.add('hidden');
    if (heading) heading.classList.add('hidden');
    if (intro) intro.classList.add('hidden');
    bridge.classList.remove('hidden');

    var list = bridge.querySelector('[data-size-bridge-highlights]');
    if (list) {
      var items = [];
      if (payload && payload.recommended_eu) {
        items.push('Recommended size: ' + formatSizeLabel(payload.recommended_eu, payload.chart));
      }
      if (payload && payload.height_fit) {
        items.push('Length: ' + heightFitLabel(payload.height_fit));
      }
      list.innerHTML = items
        .map(function (item) {
          return '<li>' + escapeHtml(item) + '</li>';
        })
        .join('');
    }

    try {
      bridge.focus({ preventScroll: true });
    } catch (e) {
      /* ignore */
    }
    try {
      bridge.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      /* ignore */
    }
    return true;
  }

  function continueAfterSizeSave(root, config, payload) {
    try {
      var highlights = [];
      if (payload && payload.recommended_eu) {
        highlights.push('Recommended size: ' + formatSizeLabel(payload.recommended_eu, payload.chart));
      }
      if (payload && payload.height_fit) {
        highlights.push('Length: ' + heightFitLabel(payload.height_fit));
      }
      sessionStorage.setItem(
        'ayami_onboarding_bridge',
        JSON.stringify({ from: 'size', highlights: highlights })
      );
    } catch (e) {
      /* ignore */
    }

    // Size is the last onboarding module → Preferences (profile).
    var prefsUrl = (config && config.nextUrl) || '/pages/preferences';
    try {
      if (
        (!config || !config.nextUrl) &&
        window.AyamiOnboardingProgress &&
        window.AyamiOnboardingProgress.accountUrl
      ) {
        prefsUrl = window.AyamiOnboardingProgress.accountUrl;
      }
    } catch (e2) {
      /* ignore */
    }
    window.location.replace(prefsUrl);
  }

  function toNumber(value) {
    if (value == null || value === '') return null;
    var n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function toCm(value, unit) {
    var n = toNumber(value);
    if (n == null) return null;
    return unit === 'in' ? n * CM_PER_INCH : n;
  }

  function fromCm(cm, unit) {
    if (cm == null) return '';
    if (unit === 'in') return Math.round((cm / CM_PER_INCH) * 10) / 10;
    return Math.round(cm * 10) / 10;
  }

  function loadCharts(url) {
    if (chartsCache) return Promise.resolve(chartsCache);
    if (chartsPromise) return chartsPromise;
    if (!url) return Promise.reject(new Error('Missing size charts URL'));
    chartsPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Could not load size charts');
        return res.json();
      })
      .then(function (data) {
        chartsCache = data;
        return data;
      });
    return chartsPromise;
  }

  // Length preference is chosen by the shopper (petite / regular / tall).
  function normalizeHeightFit(raw) {
    var fit = String(raw || '').trim().toLowerCase();
    return fit === 'petite' || fit === 'tall' || fit === 'regular' ? fit : '';
  }

  function heightFitLabel(fit) {
    if (fit === 'petite') return 'Petite';
    if (fit === 'tall') return 'Tall';
    if (fit === 'regular') return 'Regular';
    return '';
  }

  function positiveMeasure(value) {
    return value != null && Number.isFinite(value) && value > 0 ? value : null;
  }

  function measureLabel(key, chart) {
    if (key === 'chest') return chart === 'men' ? 'chest' : 'bust';
    if (key === 'waist') return 'waist';
    if (key === 'hip') return 'hip';
    return key;
  }

  function formatMeasureList(keys, chart) {
    var labels = (keys || []).map(function (key) {
      return measureLabel(key, chart);
    });
    if (labels.length <= 1) return labels[0] || '';
    if (labels.length === 2) return labels[0] + ' and ' + labels[1];
    return labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1];
  }

  var TOP_MEASURE_KEYS = ['chest', 'waist'];
  var BOTTOM_MEASURE_KEYS = ['waist', 'hip'];

  var WOMEN_EU_LETTER = {
    '32': 'XS', '34': 'XS', '36': 'S', '38': 'S', '40': 'M', '42': 'M',
    '44': 'L', '46': 'L', '48': 'XL', '50': 'XL', '52': '2XL', '54': '2XL',
    '56': '3XL', '58': '3XL', '60': '4XL', '62': '4XL',
  };
  var MEN_EU_LETTER = {
    '40': 'XS', '42': 'XS', '44': 'S', '46': 'S', '48': 'M', '50': 'M',
    '52': 'L', '54': 'L', '56': 'XL', '58': 'XL', '60': '2XL', '62': '2XL',
    '64': '3XL', '66': '3XL', '68': '4XL', '70': '4XL',
  };

  function letterForEu(chart, eu) {
    var tier = String(eu == null ? '' : eu).trim();
    if (!tier) return '';
    var map = chart === 'men' ? MEN_EU_LETTER : WOMEN_EU_LETTER;
    return map[tier] || '';
  }

  function formatSizeLabel(eu, chart, letter) {
    var size = String(eu == null ? '' : eu).trim();
    if (!size) return '';
    var mapped = letter || letterForEu(chart, size);
    return mapped ? 'EU ' + size + ' · ' + mapped : 'EU ' + size;
  }

  function chartMeasureRange(chart, key) {
    var min = null;
    var max = null;
    (chart.sizes || []).forEach(function (size) {
      var value = Number(size[key + '_cm']);
      if (!Number.isFinite(value)) return;
      if (min == null || value < min) min = value;
      if (max == null || value > max) max = value;
    });
    return { min: min, max: max };
  }

  function measuresOutsideChart(chart, measures) {
    return (measures || []).filter(function (measure) {
      var range = chartMeasureRange(chart, measure.key);
      if (range.min == null || range.max == null) return false;
      return measure.value < range.min || measure.value > range.max;
    });
  }

  function enteredMeasures(bustCm, waistCm, hipCm, allowedKeys) {
    var measures = [];
    var bust = positiveMeasure(bustCm);
    var waist = positiveMeasure(waistCm);
    var hip = positiveMeasure(hipCm);
    if (bust != null) measures.push({ key: 'chest', value: bust });
    if (waist != null) measures.push({ key: 'waist', value: waist });
    if (hip != null) measures.push({ key: 'hip', value: hip });
    if (allowedKeys && allowedKeys.length) {
      measures = measures.filter(function (measure) {
        return allowedKeys.indexOf(measure.key) !== -1;
      });
    }
    return measures;
  }

  function recommendSize(charts, chartKey, bustCm, waistCm, hipCm, allowedKeys) {
    var chart = charts && charts.charts && charts.charts[chartKey];
    if (!chart || !Array.isArray(chart.sizes) || !chart.sizes.length) return null;

    var measures = enteredMeasures(bustCm, waistCm, hipCm, allowedKeys);
    if (!measures.length) return null;

    var outside = measuresOutsideChart(chart, measures);
    if (outside.length) {
      return {
        eu: null,
        custom: true,
        used: measures.map(function (measure) {
          return measure.key;
        }),
        outside: outside.map(function (measure) {
          return measure.key;
        }),
      };
    }

    // Closest size per measure (larger EU on a tie), then take the biggest of
    // those — so a 110 hip is not pulled down to a 70 waist.
    var picked = null;
    measures.forEach(function (measure) {
      var closest = null;
      chart.sizes.forEach(function (size) {
        var chartVal = Number(size[measure.key + '_cm']);
        var eu = parseInt(size.eu, 10);
        if (!Number.isFinite(chartVal) || !Number.isFinite(eu)) return;
        var diff = Math.abs(chartVal - measure.value);
        if (
          !closest ||
          diff < closest.diff ||
          (diff === closest.diff && eu > closest.eu)
        ) {
          closest = { size: size, eu: eu, diff: diff };
        }
      });
      if (closest && (!picked || closest.eu > picked.eu)) {
        picked = closest;
      }
    });
    if (!picked) return null;
    return {
      eu: String(picked.size.eu),
      letter: picked.size.letter || letterForEu(chartKey, picked.size.eu),
      used: measures.map(function (measure) {
        return measure.key;
      }),
    };
  }

  function recommendSizes(charts, chartKey, bustCm, waistCm, hipCm) {
    return {
      overall: recommendSize(charts, chartKey, bustCm, waistCm, hipCm),
      top: recommendSize(charts, chartKey, bustCm, waistCm, hipCm, TOP_MEASURE_KEYS),
      bottom: recommendSize(charts, chartKey, bustCm, waistCm, hipCm, BOTTOM_MEASURE_KEYS),
    };
  }

  function readSavedFromPage() {
    var scriptEl = document.getElementById('SizeProfileSavedData');
    if (!scriptEl || !scriptEl.textContent.trim()) return null;
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (e) {
      return null;
    }
  }

  function loadLocal() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function saveLocal(payload) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
  }

  function normalizeProfile(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var chart = String(raw.chart || '').toLowerCase();
    if (chart !== 'women' && chart !== 'men') return null;
    var bust = toNumber(raw.bust_cm);
    var waist = toNumber(raw.waist_cm);
    var hip = toNumber(raw.hip_cm);
    if (bust == null || waist == null || hip == null) return null;
    return {
      chart: chart,
      unit: String(raw.unit || 'cm').toLowerCase() === 'in' ? 'in' : 'cm',
      bust_cm: bust,
      waist_cm: waist,
      hip_cm: hip,
      height_cm: toNumber(raw.height_cm),
      recommended_eu: raw.recommended_eu ? String(raw.recommended_eu) : null,
      height_fit: normalizeHeightFit(raw.height_fit) || null,
      updated_at: raw.updated_at || null,
      page: raw.page || '',
    };
  }

  function updateChestLabel(root) {
    var chart = root.querySelector('[data-size-chart]');
    var label = root.querySelector('[data-size-chest-label]');
    if (!chart || !label) return;
    label.textContent = chart.value === 'men' ? 'Chest' : 'Bust';
  }

  function fillForm(root, profile) {
    if (!profile) return;
    var unit = profile.unit || 'cm';
    var chartEl = root.querySelector('[data-size-chart]');
    var unitEl = root.querySelector('[data-size-unit]');
    var chestEl = root.querySelector('[data-size-chest]');
    var waistEl = root.querySelector('[data-size-waist]');
    var hipEl = root.querySelector('[data-size-hip]');
    var heightFitEl = root.querySelector('[data-size-height-fit]');
    if (chartEl) chartEl.value = profile.chart;
    if (unitEl) unitEl.value = unit;
    if (chestEl) chestEl.value = fromCm(profile.bust_cm, unit);
    if (waistEl) waistEl.value = fromCm(profile.waist_cm, unit);
    if (hipEl) hipEl.value = fromCm(profile.hip_cm, unit);
    if (heightFitEl) heightFitEl.value = normalizeHeightFit(profile.height_fit);
    updateChestLabel(root);
  }

  function readForm(root) {
    var unit = (root.querySelector('[data-size-unit]') || {}).value || 'cm';
    var chart = (root.querySelector('[data-size-chart]') || {}).value || 'women';
    var chest = toCm((root.querySelector('[data-size-chest]') || {}).value, unit);
    var waist = toCm((root.querySelector('[data-size-waist]') || {}).value, unit);
    var hip = toCm((root.querySelector('[data-size-hip]') || {}).value, unit);
    var heightFit = normalizeHeightFit(
      (root.querySelector('[data-size-height-fit]') || {}).value
    );
    return {
      chart: chart,
      unit: unit,
      bust_cm: chest,
      waist_cm: waist,
      hip_cm: hip,
      height_fit: heightFit || null,
    };
  }

  function measureHintHtml(recommendation, chart, allowedKeys, emptyCopy, contactUrl) {
    if (recommendation && recommendation.custom) {
      return (
        '<p class="size-profile__rec-slot-hint">' +
          '<a href="' +
          escapeHtml(contactUrl || '/pages/contact') +
          '">Contact us</a> — we can make it.' +
        '</p>'
      );
    }
    if (!recommendation) {
      return '<p class="size-profile__rec-slot-hint">' + escapeHtml(emptyCopy) + '</p>';
    }
    var used = recommendation.used || [];
    var remaining = (allowedKeys || []).filter(function (key) {
      return used.indexOf(key) === -1;
    });
    if (remaining.length) {
      return (
        '<p class="size-profile__rec-slot-hint">Based on ' +
        escapeHtml(formatMeasureList(used, chart)) +
        ' so far. Add ' +
        escapeHtml(formatMeasureList(remaining, chart)) +
        ' to refine.</p>'
      );
    }
    return '';
  }

  function renderRecSlot(label, recommendation, chart, allowedKeys, emptyCopy, contactUrl) {
    var isCustom = !!(recommendation && recommendation.custom);
    var hasSize = !!(recommendation && recommendation.eu);
    var sizeHtml = isCustom
      ? '<p class="size-profile__rec-slot-eu">Outside our sizes</p>'
      : hasSize
        ? '<p class="size-profile__rec-slot-eu">' +
          escapeHtml(formatSizeLabel(recommendation.eu, chart, recommendation.letter)) +
          '</p>'
        : '<p class="size-profile__rec-slot-eu">—</p>';
    return (
      '<div class="size-profile__rec-slot' +
        (hasSize || isCustom ? '' : ' is-empty') +
        (isCustom ? ' is-custom' : '') +
        '">' +
        '<p class="size-profile__rec-slot-label">' + escapeHtml(label) + '</p>' +
        sizeHtml +
        measureHintHtml(recommendation, chart, allowedKeys, emptyCopy, contactUrl) +
      '</div>'
    );
  }

  function renderRecommendation(el, recommendations, chart, contactUrl) {
    if (!el) return;
    var recs = recommendations || {};
    if (!recs.top && !recs.bottom) {
      el.classList.add('hidden');
      el.innerHTML = '';
      return;
    }
    var chestLabel = chart === 'men' ? 'chest' : 'bust';
    el.innerHTML =
      '<div class="size-profile__rec-split">' +
        renderRecSlot(
          'Tops',
          recs.top,
          chart,
          TOP_MEASURE_KEYS,
          'Add ' + chestLabel + ' or waist for a top size.',
          contactUrl
        ) +
        renderRecSlot(
          'Bottoms',
          recs.bottom,
          chart,
          BOTTOM_MEASURE_KEYS,
          'Add waist or hip for a bottom size.',
          contactUrl
        ) +
      '</div>';
    el.classList.remove('hidden');
  }

  function buildResultHtml(profile, options) {
    options = options || {};
    if (!profile) {
      return (
        '<div class="size-profile__empty">' +
          '<p>You have not saved measurements yet.</p>' +
          '<div style="margin-top:1.6rem">' +
            '<a class="button" href="' + escapeHtml(options.sizePage || '/pages/size') + '">Add measurements</a>' +
          '</div>' +
        '</div>'
      );
    }

    var html = options.savedPage ? '' : '<h2>Your size profile is saved</h2>';
    if (options.savedBanner) {
      html += '<p class="size-profile__hint" role="status">' + escapeHtml(options.savedBanner) + '</p>';
    }

    if (profile.recommended_eu) {
      html +=
        '<div class="size-profile__rec-card">' +
          '<p class="size-profile__rec-eyebrow">Recommended size</p>' +
          '<p class="size-profile__rec-eu">' +
            escapeHtml(formatSizeLabel(profile.recommended_eu, profile.chart)) +
          '</p>' +
          (profile.height_fit
            ? '<p class="size-profile__rec-length">' + escapeHtml(heightFitLabel(profile.height_fit)) + '</p>'
            : '') +
        '</div>';
    }

    html +=
      '<h3 class="size-profile__measurements-heading">Your measurements</h3>' +
      '<ul class="size-profile__summary">' +
        '<li><span>Chart</span><span>' + escapeHtml(profile.chart) + '</span></li>' +
        '<li><span>Bust / chest</span><span>' + escapeHtml(String(fromCm(profile.bust_cm, 'cm'))) + ' cm</span></li>' +
        '<li><span>Waist</span><span>' + escapeHtml(String(fromCm(profile.waist_cm, 'cm'))) + ' cm</span></li>' +
        '<li><span>Hip</span><span>' + escapeHtml(String(fromCm(profile.hip_cm, 'cm'))) + ' cm</span></li>' +
        (profile.height_fit
          ? '<li><span>Length</span><span>' + escapeHtml(heightFitLabel(profile.height_fit)) + '</span></li>'
          : '') +
      '</ul>';

    if (!options.savedPage && options.nextUrl) {
      html +=
        '<div style="margin-top:2rem">' +
          '<a class="button" href="' + escapeHtml(options.nextUrl) + '">' +
            escapeHtml(options.nextLabel || 'Continue') +
          '</a>' +
        '</div>';
    }

    if (options.savedPage && options.sizePage) {
      html +=
        '<div class="size-profile__update-actions">' +
          '<a class="button button--secondary" href="' + escapeHtml(options.sizePage) + '">Update measurements</a>' +
        '</div>';
    }

    return html;
  }

  function storePending(payload) {
    saveLocal(payload);
    try {
      sessionStorage.setItem(PENDING_SAVE_KEY, JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
  }

  function readPending() {
    try {
      var raw = sessionStorage.getItem(PENDING_SAVE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(PENDING_SAVE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function redirectToLogin(config) {
    // New Customer Accounts often ignore return_url and land on /account.
    // Flag + pending payload let /pages/size resume save → bridge when they return.
    try {
      sessionStorage.setItem(RESUME_AFTER_LOGIN_KEY, '1');
    } catch (e) {
      /* ignore */
    }

    var returnPath = config.sizePage || window.location.pathname || '/pages/size';
    try {
      var parsed = new URL(returnPath, window.location.origin);
      parsed.searchParams.set(AUTO_SAVE_PARAM, '1');
      returnPath = parsed.pathname + parsed.search + parsed.hash;
    } catch (e) {
      var separator = returnPath.indexOf('?') >= 0 ? '&' : '?';
      returnPath = returnPath + separator + AUTO_SAVE_PARAM + '=1';
    }

    var loginUrl = config.loginUrl || '/account/login';
    var q =
      'return_to=' +
      encodeURIComponent(returnPath) +
      '&return_url=' +
      encodeURIComponent(returnPath) +
      '&checkout_url=' +
      encodeURIComponent(returnPath);
    window.location.href =
      loginUrl + (loginUrl.indexOf('?') >= 0 ? '&' : '?') + q;
  }

  function postProfile(saveUrl, payload, attempt) {
    var url = saveUrl || resolveSaveUrl('');
    attempt = attempt || 0;
    return fetch(url, {
      method: 'POST',
      redirect: 'manual',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ profile: payload }),
    }).then(function (res) {
      if (
        res.type === 'opaqueredirect' ||
        res.status === 301 ||
        res.status === 302 ||
        res.status === 303 ||
        res.status === 307 ||
        res.status === 308
      ) {
        var redirectError = new Error('Save was redirected (session may have expired)');
        redirectError.status = res.status || 401;
        throw redirectError;
      }
      return res.text().then(function (text) {
        if (isHtmlResponse(text)) {
          var htmlError = new Error('Save endpoint returned HTML instead of JSON');
          htmlError.status = res.status;
          htmlError.html = true;
          throw htmlError;
        }
        var body = {};
        if (text) {
          try {
            body = JSON.parse(text);
          } catch (e) {
            var parseError = new Error('Save endpoint returned invalid JSON');
            parseError.status = res.status;
            throw parseError;
          }
        }
        if (!res.ok || !body || body.ok === false) {
          var message =
            (body && (body.detail || body.error)) ||
            'Save failed';
          if (res.status === 404 || /not found/i.test(String(message))) {
            message =
              'Size save route not found. Redeploy the color-guide-save worker, then try again.';
          }
          var error = new Error(message);
          error.status = res.status;
          error.detail = body && (body.detail || body.error);
          throw error;
        }
        return body;
      });
    }).catch(function (error) {
      if (attempt < 1 && error && (error.html || error.status === 401)) {
        return new Promise(function (resolve) {
          setTimeout(resolve, 600);
        }).then(function () {
          return postProfile(url, payload, attempt + 1);
        });
      }
      throw error;
    });
  }

  function refreshRecommendation(root, config) {
    var recEl = root.querySelector('[data-size-recommendation]');
    var formValues = readForm(root);
    var seq = ++recommendSeq;
    if (!enteredMeasures(formValues.bust_cm, formValues.waist_cm, formValues.hip_cm).length) {
      renderRecommendation(recEl, null);
      return Promise.resolve(null);
    }
    return loadCharts(config.chartsUrl).then(function (charts) {
      if (seq !== recommendSeq) return null;
      var recommendations = recommendSizes(
        charts,
        formValues.chart,
        formValues.bust_cm,
        formValues.waist_cm,
        formValues.hip_cm
      );
      renderRecommendation(recEl, recommendations, formValues.chart, config.contactUrl);
      return recommendations.overall;
    }).catch(function () {
      if (seq !== recommendSeq) return null;
      renderRecommendation(recEl, null);
      return null;
    });
  }

  function buildPayload(root, recommendation) {
    var values = readForm(root);
    return {
      chart: values.chart,
      unit: values.unit,
      bust_cm: Math.round(values.bust_cm * 10) / 10,
      waist_cm: Math.round(values.waist_cm * 10) / 10,
      hip_cm: Math.round(values.hip_cm * 10) / 10,
      height_cm: null,
      recommended_eu: recommendation && recommendation.eu ? recommendation.eu : null,
      height_fit: values.height_fit || null,
      updated_at: new Date().toISOString(),
      page: root.getAttribute('data-size-page') || window.location.pathname,
      customer_id: root.getAttribute('data-customer-id') || null,
    };
  }

  function submitForm(root, form, hintEl, resultEl, config) {
    var values = readForm(root);
    if (values.bust_cm == null || values.waist_cm == null || values.hip_cm == null) {
      if (hintEl) {
        hintEl.textContent = 'Please enter bust/chest, waist, and hip.';
        hintEl.classList.add('is-error');
      }
      return;
    }
    if (!values.height_fit) {
      if (hintEl) {
        hintEl.textContent = 'Please select petite, regular, or tall.';
        hintEl.classList.add('is-error');
      }
      return;
    }

    var submitBtn = form.querySelector('[data-size-profile-submit]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }
    if (hintEl) {
      hintEl.textContent = 'Saving…';
      hintEl.classList.remove('is-error');
    }

    refreshRecommendation(root, config)
      .then(function (recommendation) {
        var payload = buildPayload(root, recommendation);
    if (!config.loggedIn) {
      storePending(payload);
      if (hintEl) {
        hintEl.textContent =
          'Redirecting you to log in. After login, open Size again if you land in your account — we will finish saving and show the next step.';
      }
      redirectToLogin(config);
      return null;
    }
        saveLocal(payload);
        return postProfile(config.saveUrl, payload).then(function () {
          return payload;
        });
      })
      .then(function (payload) {
        if (!payload) return;
        clearPending();
        if (hintEl) {
          hintEl.textContent = '';
          hintEl.classList.remove('is-error');
        }
        continueAfterSizeSave(root, config, payload);
      })
      .catch(function (error) {
        if (error && error.status === 401) {
          var pending = buildPayload(root, null);
          storePending(pending);
          redirectToLogin(config);
          return;
        }
        if (hintEl) {
          hintEl.textContent =
            (error && (error.detail || error.message)) ||
            'Could not save. Please try again.';
          hintEl.classList.add('is-error');
        }
      })
      .finally(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
        }
      });
  }

  function initQuiz() {
    var root = document.querySelector('[data-size-profile]');
    if (!root) return;
    var form = root.querySelector('[data-size-profile-form]');
    if (!form) return;

    var hintEl = root.querySelector('[data-size-profile-hint]');
    var resultEl = root.querySelector('[data-size-profile-result]');
    var config = getConfig(root);
    var saved = normalizeProfile(readSavedFromPage()) || normalizeProfile(loadLocal());
    if (saved) fillForm(root, saved);
    updateChestLabel(root);
    refreshRecommendation(root, config);

    form.addEventListener('input', function () {
      refreshRecommendation(root, config);
    });
    form.addEventListener('change', function () {
      updateChestLabel(root);
      refreshRecommendation(root, config);
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      submitForm(root, form, hintEl, resultEl, config);
    });

    var params = new URLSearchParams(window.location.search);
    var resumeFlag = false;
    try {
      resumeFlag = sessionStorage.getItem(RESUME_AFTER_LOGIN_KEY) === '1';
    } catch (e) {
      resumeFlag = false;
    }
    var shouldResume =
      config.loggedIn &&
      (params.get(AUTO_SAVE_PARAM) === '1' || resumeFlag);

    if (shouldResume) {
      try {
        sessionStorage.removeItem(RESUME_AFTER_LOGIN_KEY);
      } catch (e) {
        /* ignore */
      }
      var pending = normalizeProfile(readPending());
      if (pending) {
        fillForm(root, pending);
        if (hintEl) {
          hintEl.textContent = 'Finishing your save…';
          hintEl.classList.remove('is-error');
        }
        submitForm(root, form, hintEl, resultEl, config);
      }
      try {
        var clean =
          window.location.pathname +
          window.location.search
            .replace(new RegExp('([?&])' + AUTO_SAVE_PARAM + '=1&?'), '$1')
            .replace(/[?&]$/, '');
        window.history.replaceState({}, '', clean || window.location.pathname);
      } catch (e) {
        /* ignore */
      }
    }
  }

  function initSavedPage() {
    var root = document.querySelector('[data-size-profile-saved]');
    if (!root) return;
    var target = root.querySelector('[data-size-profile-saved-content]');
    if (!target) return;

    var config = {
      loggedIn: root.getAttribute('data-customer-logged-in') === 'true',
      sizePage: root.getAttribute('data-size-page') || '/pages/size',
      loginUrl: root.getAttribute('data-login-url') || '/account/login',
      registerUrl: root.getAttribute('data-register-url') || '/account/register',
    };

    if (!config.loggedIn) {
      target.innerHTML =
        '<p>Log in to view your saved measurements.</p>' +
        '<div style="margin-top:1.6rem;display:flex;gap:1rem;justify-content:center;flex-wrap:wrap">' +
          '<a class="button" href="' + escapeHtml(config.loginUrl) + '">Log in</a>' +
          '<a class="button button--secondary" href="' + escapeHtml(config.registerUrl) + '">Create account</a>' +
        '</div>';
      if (typeof window.kailaRefreshPreferencesHub === 'function') {
        window.kailaRefreshPreferencesHub();
      }
      return;
    }

    var profile = normalizeProfile(readSavedFromPage()) || normalizeProfile(loadLocal());
    if (profile) saveLocal(profile);
    var params = new URLSearchParams(window.location.search);
    target.innerHTML = buildResultHtml(profile, {
      savedPage: true,
      sizePage: config.sizePage,
      savedBanner: params.get('size_saved') === '1' ? 'Your measurements have been saved.' : '',
    });

    if (typeof window.kailaRefreshPreferencesHub === 'function') {
      window.kailaRefreshPreferencesHub();
    }
  }

  function boot() {
    initQuiz();
    initSavedPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
